import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  Search,
  Eye,
  Loader2,
  AlertCircle,
  AlertTriangle,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Layers,
  BookOpen,
  FolderOpen,
  PlayCircle,
  Package,
  Plus,
  Tag,
  ExternalLink,
  ListTree,
  Star
} from 'lucide-react'
import { adminApi, AdminContent, HierarchyLevel, ContentStatus } from '@/api/admin'
import CurriculumTreeViewer from '@/components/common/CurriculumTreeViewer'

const HIERARCHY_LEVELS: HierarchyLevel[] = ['program', 'curriculum', 'course', 'module', 'lesson']
const CONTENT_STATUSES: ContentStatus[] = ['draft', 'ready', 'published', 'archived', 'legacy']

const LEVEL_LABELS: Record<HierarchyLevel, string> = {
  program: '프로그램',
  curriculum: '커리큘럼',
  course: '코스',
  module: '모듈',
  lesson: '레슨'
}

const LEVEL_ICONS: Record<HierarchyLevel, React.ElementType> = {
  program: Layers,
  curriculum: BookOpen,
  course: FolderOpen,
  module: FolderOpen,
  lesson: PlayCircle
}

const STATUS_LABELS: Record<ContentStatus, string> = {
  draft: '초안',
  ready: '준비됨',
  published: '발행됨',
  archived: '보관됨',
  legacy: '레거시'
}

const STATUS_COLORS: Record<ContentStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  ready: 'bg-yellow-100 text-yellow-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-red-100 text-red-700',
  legacy: 'bg-orange-100 text-orange-700'
}

const LEVEL_COLORS: Record<HierarchyLevel, string> = {
  program: 'bg-purple-100 text-purple-700',
  curriculum: 'bg-blue-100 text-blue-700',
  course: 'bg-cyan-100 text-cyan-700',
  module: 'bg-teal-100 text-teal-700',
  lesson: 'bg-emerald-100 text-emerald-700'
}

interface ContentDetailModalProps {
  content: AdminContent
  onClose: () => void
}

function ContentDetailModal({ content, onClose }: ContentDetailModalProps) {
  const Icon = LEVEL_ICONS[content.hierarchyLevel as HierarchyLevel]

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${LEVEL_COLORS[content.hierarchyLevel as HierarchyLevel]}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{content.title}</h2>
              <p className="text-sm text-slate-500">ID: {content.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status & Level */}
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${LEVEL_COLORS[content.hierarchyLevel as HierarchyLevel]}`}>
              {LEVEL_LABELS[content.hierarchyLevel as HierarchyLevel]}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[content.status as ContentStatus]}`}>
              {STATUS_LABELS[content.status as ContentStatus]}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${content.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {content.isActive ? '활성' : '비활성'}
            </span>
            {content.origin && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${content.origin === 'direct' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                {content.origin === 'direct' ? 'Direct' : 'Notion'}
              </span>
            )}
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-500">Bare Title</label>
              <p className="text-slate-900">{content.bareTitle || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">QID</label>
              <p className="text-slate-900 font-mono">{content.qid || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Notion Page ID</label>
              <p className="text-slate-900 font-mono text-xs">{content.notionPageId || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">예상 학습 시간</label>
              <p className="text-slate-900">
                {content.estimatedDurationMinutes ? `${content.estimatedDurationMinutes}분` : '-'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Parent ID</label>
              <p className="text-slate-900">{content.parentId || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">별점</label>
              <div className="flex items-center gap-0.5 mt-0.5">
                {content.starRating ? (
                  <>
                    {Array.from({ length: content.starRating }, (_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                    {Array.from({ length: Math.max(0, 5 - content.starRating) }, (_, i) => (
                      <Star key={`empty-${i}`} className="w-4 h-4 text-slate-200" />
                    ))}
                    <span className="ml-1 text-sm text-slate-500">({content.starRating})</span>
                  </>
                ) : (
                  <span className="text-slate-900">-</span>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">전체 순서 (Overall)</label>
              <p className="text-slate-900 font-mono">{content.overallSequenceOrder ?? '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">부모 내 순서</label>
              <p className="text-slate-900 font-mono">{content.sequenceOrderInParent ?? '-'}</p>
            </div>
          </div>

          {/* Description */}
          {content.description && (
            <div>
              <label className="text-sm font-medium text-slate-500">설명</label>
              <p className="text-slate-900 mt-1">{content.description}</p>
            </div>
          )}

          {/* Timestamps */}
          <div className="pt-4 border-t border-slate-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <label className="text-slate-500">생성일</label>
                <p className="text-slate-900">{new Date(content.createdAt).toLocaleString('ko-KR')}</p>
              </div>
              <div>
                <label className="text-slate-500">수정일</label>
                <p className="text-slate-900">{new Date(content.updatedAt).toLocaleString('ko-KR')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-200">
          <button onClick={onClose} className="btn-secondary btn-md w-full">
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// 상품 표시 컴포넌트
function LinkedProducts({ contentId, contentTitle }: { contentId: number; contentTitle: string }) {
  const navigate = useNavigate()
  
  const { data: products = [], isLoading } = useQuery({
    queryKey: ['admin', 'contents', contentId, 'products'],
    queryFn: () => adminApi.getProductsByContentId(contentId),
    staleTime: 30 * 1000,
  })
  
  const formatPrice = (price: number) =>
    new Intl.NumberFormat('ko-KR').format(price)
  
  const handleCreateProduct = () => {
    // 커리큘럼 ID를 쿼리 파라미터로 전달하여 상품 관리 페이지로 이동
    navigate(`/products?createFor=${contentId}&curriculumTitle=${encodeURIComponent(contentTitle)}`)
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">상품 정보 로딩 중...</span>
      </div>
    )
  }
  
  return (
    <div className="mt-4 pt-4 border-t border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Package className="w-4 h-4 text-amber-600" />
          연결된 상품 ({products.length})
        </h4>
        <button
          onClick={handleCreateProduct}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-md transition-colors"
          title="이 커리큘럼으로 새 상품 만들기"
        >
          <Plus className="w-3 h-3" />
          새 상품
        </button>
      </div>
      
      {products.length === 0 ? (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
          <Package className="w-8 h-8 text-slate-300" />
          <div>
            <p className="text-sm text-slate-500">연결된 상품이 없습니다</p>
            <button
              onClick={handleCreateProduct}
              className="text-xs text-amber-600 hover:text-amber-700 font-medium mt-1"
            >
              지금 상품 만들기 →
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {products.map((product) => (
            <div
              key={product.id}
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-amber-100 rounded-lg">
                  <Tag className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{product.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                      product.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {product.isActive ? '활성' : '비활성'}
                    </span>
                    {product.pricings && product.pricings.length > 0 && (
                      <span className="text-xs text-slate-500">
                        {product.pricings[0].isNegotiable ? (
                          '별도 협의'
                        ) : (
                          `₩${formatPrice(product.pricings[0].price)} ~`
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate(`/products?highlight=${product.id}`)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded"
                title="상품 관리에서 보기"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// 콘텐츠 확장 정보 (별점, 학습목표, 요약, 주요내용, Concepts)
function ContentExpandedInfo({ content }: { content: AdminContent }) {
  const hasAnyInfo = content.starRating || content.learningObjectives || content.shortDescription || content.description || content.concepts

  if (!hasAnyInfo) return null

  return (
    <div className="space-y-3 mb-2">
      {/* 별점 */}
      {content.starRating != null && content.starRating > 0 && (
        <div className="flex items-center gap-1.5">
          {Array.from({ length: content.starRating }, (_, i) => (
            <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
          ))}
          {Array.from({ length: Math.max(0, 5 - content.starRating) }, (_, i) => (
            <Star key={`empty-${i}`} className="w-4 h-4 text-slate-200" />
          ))}
          <span className="ml-1 text-xs text-slate-500">({content.starRating})</span>
        </div>
      )}

      {/* 학습목표 */}
      {content.learningObjectives && (
        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">■ 학습목표</h4>
          <p className="text-sm text-slate-700 whitespace-pre-line">{content.learningObjectives}</p>
        </div>
      )}

      {/* 요약 */}
      {content.shortDescription && (
        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">■ 요약</h4>
          <p className="text-sm text-slate-700">{content.shortDescription}</p>
        </div>
      )}

      {/* 주요내용 */}
      {content.description && (
        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">■ 주요내용</h4>
          <p className="text-sm text-slate-700 whitespace-pre-line">{content.description}</p>
        </div>
      )}

      {/* Concepts */}
      {content.concepts && (
        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">■ Concepts</h4>
          <div className="flex flex-wrap gap-1.5">
            {content.concepts.split(',').map((concept, i) => (
              <span
                key={i}
                className="px-2 py-0.5 text-xs bg-indigo-50 text-indigo-700 rounded-full font-medium"
              >
                {concept.trim()}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// 커리큘럼 상세 패널 (LinkedProducts만 표시 - Configuration은 Product 레벨에서 관리)
function CurriculumDetailPanel({ contentId, contentTitle }: { contentId: number; contentTitle: string }) {
  return (
    <>
      {/* 연결된 상품 */}
      <LinkedProducts contentId={contentId} contentTitle={contentTitle} />
    </>
  )
}

export function ContentManagement() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [filterLevel, setFilterLevel] = useState<HierarchyLevel | ''>('curriculum')
  const [filterStatus, setFilterStatus] = useState<ContentStatus | ''>('')
  const [selectedContent, setSelectedContent] = useState<AdminContent | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [treeViewerContentId, setTreeViewerContentId] = useState<number | null>(null)
  const [treeViewerTitle, setTreeViewerTitle] = useState<string>('')
  const [editingStatusId, setEditingStatusId] = useState<number | null>(null)
  const statusDropdownRef = useRef<HTMLDivElement>(null)

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setEditingStatusId(null)
      }
    }
    if (editingStatusId !== null) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [editingStatusId])

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'contents', { page, keyword: searchKeyword, filterLevel, filterStatus }],
    queryFn: () => adminApi.getContents({
      page,
      size: 20,
      keyword: searchKeyword || undefined,
      hierarchyLevel: filterLevel || undefined,
      status: filterStatus || undefined
    }),
    staleTime: 30 * 1000,
  })

  // 콘텐츠 계층 구조 조회
  const { data: hierarchyData, isLoading: isHierarchyLoading } = useQuery({
    queryKey: ['content-hierarchy', treeViewerContentId],
    queryFn: () => adminApi.getContentHierarchy(treeViewerContentId!),
    enabled: !!treeViewerContentId,
  })

  // 개별 상태 변경 (published <-> draft 토글)
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ContentStatus }) =>
      adminApi.updateContentStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'contents'] })
    },
    onError: (error) => {
      console.error('상태 변경 실패:', error)
      alert('상태 변경에 실패했습니다.')
    },
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchKeyword(keyword)
    setPage(0)
  }

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">콘텐츠 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-amber-600" />
            콘텐츠 관리
          </h1>
          <p className="text-slate-600 mt-1">총 {data?.totalElements ?? 0}개의 콘텐츠</p>
        </div>
        <button
          onClick={() => alert(`다음을 terminal에서 실행하세요.\n\n% cd <repository>/migration-worker\n% ./migrate_from_notion.sh --help`)}
          className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium"
        >
          Sync from Notion
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="제목, 설명 검색..."
                className="input pl-10"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
          </div>
          <select
            className="input w-36"
            value={filterLevel}
            onChange={(e) => { setFilterLevel(e.target.value as HierarchyLevel | ''); setPage(0) }}
          >
            <option value="">모든 계층</option>
            {HIERARCHY_LEVELS.map((level) => (
              <option key={level} value={level}>{LEVEL_LABELS[level]}</option>
            ))}
          </select>
          <select
            className="input w-32"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value as ContentStatus | ''); setPage(0) }}
          >
            <option value="">모든 상태</option>
            {CONTENT_STATUSES.map((status) => (
              <option key={status} value={status}>{STATUS_LABELS[status]}</option>
            ))}
          </select>
          <button type="submit" className="btn-primary btn-md">
            검색
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="w-10 px-4 py-4"></th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">계층 (ID)</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">
                  <span className="flex items-center gap-1.5">
                    제목
                    <span className="text-slate-400">(</span>
                    <ListTree className="w-3.5 h-3.5 text-purple-500" />
                    <span className="text-slate-400 text-xs">구조보기</span>
                    <span className="text-slate-400">)</span>
                  </span>
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">상태</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.map((content) => {
                const Icon = LEVEL_ICONS[content.hierarchyLevel as HierarchyLevel]
                const isExpanded = expandedRows.has(content.id)

                return (
                  <>
                    <tr key={content.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4">
                        <button
                          onClick={() => toggleRow(content.id)}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className={`p-1 rounded ${LEVEL_COLORS[content.hierarchyLevel as HierarchyLevel]}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="text-sm font-medium">
                            {LEVEL_LABELS[content.hierarchyLevel as HierarchyLevel]}
                          </span>
                          <span className="text-xs text-slate-400">({content.id})</span>
                          {content.origin === 'direct' && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-600">Direct</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <p className="text-sm font-medium text-slate-900 max-w-xs truncate">{content.title}</p>
                          {content.hierarchyLevel !== 'lesson' && (
                            <button
                              onClick={() => {
                                setTreeViewerContentId(content.id)
                                setTreeViewerTitle(content.title || '콘텐츠 구조')
                              }}
                              className="p-1 hover:bg-red-50 rounded text-purple-500 hover:text-red-500 flex-shrink-0"
                              title="콘텐츠 구조 보기"
                            >
                              <ListTree className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="relative" ref={editingStatusId === content.id ? statusDropdownRef : undefined}>
                          <button
                            onClick={() => setEditingStatusId(editingStatusId === content.id ? null : content.id)}
                            className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer transition-all hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 ${STATUS_COLORS[content.status as ContentStatus]}`}
                            title="클릭하여 상태 변경"
                          >
                            {STATUS_LABELS[content.status as ContentStatus]}
                            <ChevronDown className="w-3 h-3 inline ml-1" />
                          </button>
                          {editingStatusId === content.id && (
                            <div className="absolute z-30 mt-1 left-0 w-48 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                              <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-start gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                                <p className="text-[10px] leading-tight text-amber-700">
                                  주의: Notion에서도 함께 바꿔주세요. Overwrite될 수 있습니다.
                                </p>
                              </div>
                              {CONTENT_STATUSES.map((s) => (
                                <button
                                  key={s}
                                  onClick={() => {
                                    if (s !== content.status) {
                                      statusMutation.mutate({ id: content.id, status: s })
                                    }
                                    setEditingStatusId(null)
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${
                                    s === content.status ? 'bg-slate-50 font-semibold' : ''
                                  }`}
                                >
                                  <span className={`inline-block w-2 h-2 rounded-full ${STATUS_COLORS[s].split(' ')[0]}`} />
                                  {STATUS_LABELS[s]}
                                  {s === content.status && (
                                    <span className="ml-auto text-xs text-slate-400">현재</span>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          {/* 커리큘럼인 경우 상품 만들기 버튼 표시 */}
                          {content.hierarchyLevel === 'curriculum' && (
                            <button
                              onClick={() => navigate(`/products?createFor=${content.id}&curriculumTitle=${encodeURIComponent(content.title)}`)}
                              className="p-2 hover:bg-amber-50 rounded-lg text-amber-600"
                              title="이 커리큘럼으로 새 상품 만들기"
                            >
                              <Package className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => setSelectedContent(content)}
                            className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                            title="상세 보기"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${content.id}-details`} className="bg-slate-50">
                        <td colSpan={5} className="px-6 py-4">
                          <div className="ml-32">
                          {/* 콘텐츠 상세 정보 */}
                          <ContentExpandedInfo content={content} />
                          
                          {/* 커리큘럼인 경우 상품 + Configuration 표시 */}
                          {content.hierarchyLevel === 'curriculum' && (
                            <CurriculumDetailPanel contentId={content.id} contentTitle={content.title} />
                          )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {data?.content.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">등록된 콘텐츠가 없습니다</p>
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalElements)} / {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={data.first}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={data.last}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedContent && (
        <ContentDetailModal
          content={selectedContent}
          onClose={() => setSelectedContent(null)}
        />
      )}

      {/* 콘텐츠 트리 뷰어 */}
      {hierarchyData && (
        <CurriculumTreeViewer
          data={hierarchyData}
          isOpen={!!treeViewerContentId}
          onClose={() => setTreeViewerContentId(null)}
          title={treeViewerTitle}
        />
      )}

      {/* 트리 뷰어 로딩 중 */}
      {treeViewerContentId && isHierarchyLoading && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
            <span>콘텐츠 구조 로딩 중...</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

